import React from "react";
import { Draggable, type DraggableChildrenFn, Droppable } from "react-beautiful-dnd";
import { fetchQuery, graphql } from "react-relay";
import { useHistory } from "react-router";

import { faArrowLeft, faArrowRight, faCalendarRange } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { getHours, getMinutes, isThisMonth, set, startOfMinute } from "date-fns";
import { compact } from "lodash";

import type { EventFormResult } from "~/calendar/EventForm";
import { DailyUpdater } from "~/lib/dailyUpdate";
import moment from "~/lib/extendedMoment";
import { useDebouncedValue, useMedia } from "~/lib/hooks";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { environment } from "~/models";
import MeetingLink from "~/nav/panels/Home/Links/MeetingLink";
import PanelSection from "~/nav/panels/shared/PanelSection";
import { Button, LoadingSpinner, Tooltip, useModals } from "~/ui";
import DateSelector from "~/ui/DateSelector";
import IconButton from "~/ui/legacy/IconButton";

import { CalendarObserver } from "../../Meetings";
import { EventFormModalWithoutViewer } from "../../Meetings/EventFormModal/EventFormModal";
import EmptyState from "./EmptyState";

import type { HomePanelGroup_group$data as HomePanelGroupFragment } from "~/__generated__/HomePanelGroup_group.graphql";
import type { MeetingsGroupEventsQuery } from "~/__generated__/MeetingsGroupEventsQuery.graphql";

import styles from "./MeetingsGroup.module.css";

const meetingsGroupEventsQuery = graphql`
	query MeetingsGroupEventsQuery($end: DateTime, $start: DateTime) {
		dayEvents: eventsByUserEventFilters(first: null, after: null, start: $start, end: $end)
			@connection(key: "HomePanel_dayEvents", filters: []) {
			edges {
				node {
					id
					start
					end
					title
					guid
					note {
						stream {
							viewerLastSeenAt
							latestNoteEditedAt
						}
					}
					...MeetingLink_meeting
				}
			}
		}
	}
`;

function MeetingsGroupEvents({
	isUIDateAndDataDateDifferent,
	queryVariables,
	emptyStateText,
	isDropDisabled,
	group,
	setUnread,
	refetch,
}: {
	isUIDateAndDataDateDifferent?: boolean;
	queryVariables: MeetingsGroupEventsQuery["variables"];
	refetch: () => void;
	emptyStateText: string;
	group: { id: string };
	isDropDisabled?: boolean;
	setUnread: (v: boolean) => void;
}) {
	const data = useLazyLoadQuery<MeetingsGroupEventsQuery>(meetingsGroupEventsQuery, queryVariables, {
		fetchPolicy: "store-and-network",
		fetchKey: queryVariables.start || "",
	});

	const dateNumber = (queryVariables.start ? new Date(queryVariables.start) : new Date()).getDate();

	const events = React.useMemo(() => compact(data.dayEvents.edges).map(edge => edge?.node), [data.dayEvents.edges]);
	const sorted = [...events].sort((a, b) => +(a.start ? new Date(a.start) : 0) - +(b.start ? new Date(b.start) : 0));
	const draggableInnerRenderer = React.useCallback<DraggableChildrenFn>(
		(provided, snapshot, rubric) => {
			const meeting = sorted[rubric.source.index];
			// Relay cache isn't working as intended
			// We see out of range events from previous query showing up.
			// Style them differently so users know we are loading the correct ones.
			const isOutOfRange =
				isUIDateAndDataDateDifferent ||
				!!(
					meeting.end &&
					new Date(meeting.end).getDate() !== dateNumber &&
					meeting.start &&
					new Date(meeting.start).getDate() !== dateNumber
				);
			return (
				<>
					<MeetingLink
						refetchMeetingDataFunc={refetch}
						meeting={sorted[rubric.source.index]}
						disabled={isOutOfRange}
						{...provided.draggableProps}
						{...provided.dragHandleProps}
						innerRef={provided.innerRef}
					/>
				</>
			);
		},
		[refetch, sorted, dateNumber, isUIDateAndDataDateDifferent],
	);

	const unread = sorted.some(function itemIsUnread(event) {
		if (
			Boolean(
				event.note?.stream.latestNoteEditedAt &&
					moment(event.note?.stream.viewerLastSeenAt).isBefore(event.note?.stream.latestNoteEditedAt),
			)
		) {
			return event;
		}

		return;
	});

	React.useEffect(() => {
		setUnread(unread);
	}, [setUnread, unread]);

	const supportsHover = useMedia("(hover: hover)");

	return (
		<>
			{sorted.length === 0 ? (
				<EmptyState emptyStateText={emptyStateText} />
			) : (
				<Droppable
					type="ITEM"
					droppableId={group.id}
					renderClone={draggableInnerRenderer}
					isDropDisabled={isDropDisabled}
				>
					{provided => (
						<div {...provided.droppableProps} ref={provided.innerRef}>
							{sorted.length === 0 ? (
								<EmptyState emptyStateText={emptyStateText} />
							) : (
								sorted.map((event, index) => (
									<Draggable key={event.id} draggableId={event.id} index={index} isDragDisabled={!supportsHover}>
										{draggableInnerRenderer}
									</Draggable>
								))
							)}
							{provided.placeholder}
						</div>
					)}
				</Droppable>
			)}
		</>
	);
}

interface MeetingsGroupProps extends React.HTMLProps<HTMLDivElement> {
	group: HomePanelGroupFragment;
	isDragging: boolean;
	isDropDisabled?: boolean;
	dropDisabledFade?: boolean;
}

const MeetingsGroup = React.forwardRef<HTMLDivElement, MeetingsGroupProps>(function MeetingsGroup(
	{ group, isDragging, isDropDisabled, ...rest },
	innerRef,
) {
	const [unread, setUnread] = React.useState(false);
	const [date, setDate] = React.useState<Date>(new Date());
	const debouncedDate = useDebouncedValue(date, 200);
	const eventsQueryVariables: MeetingsGroupEventsQuery["variables"] = React.useMemo(() => {
		const startOfDay = new Date(debouncedDate.valueOf());
		const endOfDay = new Date(debouncedDate.valueOf());
		startOfDay.setHours(0, 0, 0, 0);
		endOfDay.setHours(23, 59, 59, 999);

		const start = startOfDay.toISOString();
		const end = endOfDay.toISOString();
		return {
			start: start,
			end: end,
		};
	}, [debouncedDate]);

	const refetchEvents = React.useCallback(() => {
		fetchQuery(environment, meetingsGroupEventsQuery, eventsQueryVariables, {
			fetchPolicy: "network-only",
		}).toPromise();
	}, [eventsQueryVariables]);

	const history = useHistory();
	const modals = useModals();

	React.useEffect(() => {
		function triggerUpdate() {
			setDate(new Date());
		}

		const updater = new DailyUpdater(triggerUpdate);

		updater.start();
		return () => updater.stop();
	}, []);

	const onDateBack = React.useCallback(() => {
		setDate(date => {
			const clone = new Date(date.valueOf());
			clone.setDate(clone.getDate() - 1);
			return clone;
		});
	}, []);

	const onDateForward = React.useCallback(() => {
		setDate(date => {
			const clone = new Date(date.valueOf());
			clone.setDate(clone.getDate() + 1);
			return clone;
		});
	}, []);

	const onEventFormSaved = React.useCallback(
		(closeModal: () => void) => (result: EventFormResult) => {
			switch (result.type) {
				case "cancelled":
					return closeModal();
				case "succeeded": {
					refetchEvents();
					history.push(`/meetings/${result.event.eventGuid}/`);
					return;
				}
				default:
					return;
			}
		},
		[history, refetchEvents],
	);

	const openAddEventModal = React.useCallback(() => {
		const now = new Date();
		modals.open(({ close }) => (
			<React.Suspense fallback={null}>
				<EventFormModalWithoutViewer
					onFormFinish={onEventFormSaved(close)}
					initialData={{
						start: set(startOfMinute(date), {
							hours: getHours(now),
							minutes: getMinutes(now),
						}),
					}}
				/>
			</React.Suspense>
		));
	}, [modals, onEventFormSaved, date]);

	const emptyStateText = React.useMemo(() => {
		const clone = new Date(date);
		clone.setHours(0, 0, 0);

		const today = new Date();
		today.setHours(0, 0, 0);

		if (clone === today) return "No meetings today";

		return `No meetings on ${date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}`;
	}, [date]);

	React.useEffect(() => {
		CalendarObserver.getInstance().observe("HomePanel", events => {
			if (!events) refetchEvents();
		});
		return () => CalendarObserver.getInstance().unobserve("HomePanel");
	}, [refetchEvents]);

	const calendarButtonLabel = React.useMemo(() => {
		if (isThisMonth(date)) {
			const now = new Date();
			const daysFromNow = date.getDate() - now.getDate();

			if (daysFromNow === 1) return "Tomorrow";
			if (daysFromNow === 0) return "Today";
			if (daysFromNow === -1) return "Yesterday";
		}

		return date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
	}, [date]);

	return (
		<PanelSection
			hideHomePanelGroupEllipsis
			onPlusClick={openAddEventModal}
			plusButtonTooltip="Create event"
			title="Meetings"
			icon={faCalendarRange}
			unread={unread}
			{...rest}
			ref={innerRef}
			isDragging={isDragging}
			group={group}
			beforePlusButton={
				<>
					<DateSelector renderTodayButton date={date} setDate={setDate}>
						<Tooltip content="View Calendar" placement="top">
							<span>
								<Button variant="tertiary" size="slim" className={styles.dateSelector}>
									{calendarButtonLabel}
								</Button>
							</span>
						</Tooltip>
					</DateSelector>
					<div className={styles.arrows}>
						<IconButton title="Previous Day" icon={faArrowLeft} onClick={onDateBack} className={styles.arrowIcon} />
						<IconButton title="Next Day" icon={faArrowRight} onClick={onDateForward} className={styles.arrowIcon} />
					</div>
				</>
			}
			data-test-id="home-panel-meetings"
		>
			<React.Suspense
				// TODO: better look
				fallback={
					<div style={{ position: "relative", minHeight: "100px" }}>
						<LoadingSpinner inline />
					</div>
				}
			>
				<MeetingsGroupEvents
					refetch={refetchEvents}
					queryVariables={eventsQueryVariables}
					emptyStateText={emptyStateText}
					group={group}
					isDropDisabled={isDropDisabled}
					setUnread={setUnread}
					isUIDateAndDataDateDifferent={debouncedDate.toDateString() !== date.toDateString()}
				/>
			</React.Suspense>
		</PanelSection>
	);
});

export default MeetingsGroup;
